import React, {useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback, memo} from 'react';
import parse from 'html-react-parser';

import {
    Box,
    Typography,
    Chip,
    Button,
    CircularProgress,
    Tooltip,
} from '@mui/material';

import {Message} from '../../components/Message/Message';
import {Details} from '../../common/Details/Details';

import {scrollToElement} from '../../utils/scrollToElement';


import {translates} from '../../constants/translates';

import {ReactComponent as AnimatedDotsIcon} from '../../assets/icons/animatedDots.svg';
import {useChat} from "../../context/Context";
import {hasRegularMessage} from '../../utils/hasRegularMessage';

const chatBodyStyles = {
    pl: 2.86,
    pr: 0,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
};

const chatDialogStyles = {
    overflowY: 'auto',
    flex: '1 0 100px',
    pr: 2.86,
    pt: 1.45,
    display: 'flex',
    flexDirection: 'column',
};

export const ChatBody = memo(
    ({
         messages,
         messagesPreload,
         hasPreloadedMessages,
         sendMessageWithActiveDialog,
         showRequest,
         isChatActive,
         dialogId,
         sendMessage,
         onClick = () => {
         },
         operatorsList = [],
     }) => {
        const {defaultLang, titleMessages, visitorMessages, isDialogActive, isDialogStateReady, socketInstance} = useChat();

        const [isCompanionWriting, setIsCompanionWriting] = useState(false);
        const [messageText, setMessageText] = useState('');

        const historyAnchorRef = useRef(null);
        const skipAutoScrollRef = useRef(false);

        useEffect(() => {
            if (messageText) {
                sendMessageWithActiveDialog(sendMessageHandler);
            }
            // return () => setMessageText('');
        }, [messageText, dialogId]);

        useEffect(() => {
            if (skipAutoScrollRef.current) {
                skipAutoScrollRef.current = false;
                return;
            }

            const bodyElement = document.querySelector('.chatBody');
            const selectedElementClasses = bodyElement?.lastChild.classList;
            const lastBodyElement =
                selectedElementClasses[selectedElementClasses.length - 1];
            scrollToElement(lastBodyElement);
        }, [messages, isCompanionWriting, isChatActive, showRequest]);

        useEffect(() => {
            socketInstance.on('dialog.printing.get', (data) => {
                setIsCompanionWriting(data.status === 'start');
            });
        }, [isChatActive]);

        const sortedMessages = useMemo(() => {
            const normalize = (msg) => ({
                ...msg,
                created_at: msg?.created_at || msg?.time || msg?.updated_at,
                isSystem: msg?.isSystem || msg?.system || msg?.is_system,
            });

            const getDateValue = (msg) => {
                const val = msg?.created_at || msg?.time || msg?.updated_at;
                const date = val ? new Date(val) : new Date();
                return Number.isNaN(date.getTime()) ? new Date() : date;
            };

            return [...messages.map(normalize)].sort(
                (a, b) => getDateValue(a) - getDateValue(b)
            );
        }, [messages]);

        const historyPreloadHandler = useCallback(() => {
            const bodyElement = document.querySelector('.chatBody');

            if (bodyElement) {
                historyAnchorRef.current = {
                    scrollHeight: bodyElement.scrollHeight,
                    scrollTop: bodyElement.scrollTop,
                    firstId: sortedMessages?.[0]?.id ?? null,
                };
            }

            messagesPreload?.();
        }, [messagesPreload, sortedMessages]);

        useLayoutEffect(() => {
            const anchor = historyAnchorRef.current;
            if (!anchor) return;

            const firstId = sortedMessages?.[0]?.id ?? null;
            if (firstId === anchor.firstId) return;

            const bodyElement = document.querySelector('.chatBody');
            if (!bodyElement) return;

            const delta = bodyElement.scrollHeight - anchor.scrollHeight;
            if (delta <= 0) return;

            historyAnchorRef.current = null;
            skipAutoScrollRef.current = true;
            bodyElement.scrollTop = anchor.scrollTop + delta;
        }, [sortedMessages]);

        const sendMessageHandler = useCallback(
            (id) => {
                sendMessage(messageText, id);
                setMessageText('');
            },
            [messageText]
        );

        const formatSystemTime = useCallback((value) => {
            if (!value) return '';
            const parsed = new Date(value);
            if (!Number.isNaN(parsed.getTime())) {
                return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            return value;
        }, []);

        return (
            <Box sx={chatBodyStyles} id="widget-body">
                <Box sx={chatDialogStyles} className="chatBody">
                    {isDialogStateReady && visitorMessages && !messages?.length && !isDialogActive && !showRequest ? (
                        <DialogStartScreen
                            titleMessages={titleMessages}
                            visitorMessages={visitorMessages}
                            defaultLang={defaultLang}
                            onSendStartMessage={(text) => {
                                setMessageText(text);
                            }}
                        />
                    ) : (
                        <>
                            {hasPreloadedMessages && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mt: '2px',
                                        mb: '4px',
                                        color: 'primary.text',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    }}
                                    onClick={historyPreloadHandler}
                                >
                                    {translates[defaultLang].show_history}
                                </Box>
                            )}
                            {sortedMessages?.map((message, index, arr) => {
                                if (message?.isSystem) {
                                    return (
                                        <SystemMessage
                                            key={message.id || `${message.created_at}-${index}`}
                                            text={message?.text}
                                            time={formatSystemTime(message?.time || message?.created_at)}
                                            color={message?.color}
                                        />
                                    );
                                }

                                return (
                                    <Message
                                        key={message.id || `${message.created_at}-${index}`}
                                        defaultLang={defaultLang}
                                        className="message"
                                        message={message}
                                        prevMessage={arr[index - 1]}
                                        isMine={!message?.user}
                                        isSingle={arr[index + 1]?.user?.id === message?.user?.id}
                                    />
                                );
                            })}
                            {isDialogStateReady && !hasRegularMessage(messages) && visitorMessages && !showRequest && (
                                <DialogStartScreen
                                    titleMessages={titleMessages}
                                    visitorMessages={visitorMessages}
                                    defaultLang={defaultLang}
                                    showGreeting={false}
                                    onSendStartMessage={setMessageText}
                                />
                            )}
                            {isCompanionWriting && <Typing defaultLang={defaultLang}/>}
                            {!!operatorsList?.length && (
                                <ArntFreeOperanors defaultLang={defaultLang}/>
                            )}
                            {showRequest && (
                                <ActionBlock onClick={onClick} defaultLang={defaultLang}/>
                            )}
                            <Box sx={{height: '0px'}}/>
                        </>
                    )}
                </Box>
            </Box>
        );
    }
);

const DialogStartScreen = ({
                               titleMessages,
                               defaultLang,
                               onSendStartMessage,
                               visitorMessages,
                               showGreeting = true,
                               inSchedule = true,
                           }) => {
    const [isMessageSended, setMessageSended] = useState(false);

    return (
        <>
            <Box

                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: !inSchedule ? 'center' : 'left',
                    height: showGreeting ? '100%' : 'auto',
                }}
            >
                {!inSchedule ? (
                    <>
                        <Typography
                            fontSize={16}
                            color={'header.main'}

                            sx={(theme) => ({
                                mb: 1.45,
                                fontFamily: theme.typography.fontFamily,
                            })}
                            component="div"
                        >
                            {titleMessages?.work?.greeting[defaultLang]}
                        </Typography>
                        <Typography
                            sx={(theme) => ({
                                mb: 1.9,
                                fontSize: '14px',
                                fontFamily: theme.typography.fontFamily,
                            })}
                        >
                            {titleMessages?.non_work?.greeting[defaultLang] ? parse(titleMessages?.non_work?.greeting[defaultLang]) : ''}
                        </Typography>
                    </>
                ) : (
                    <>
                        {showGreeting && <>
                        <Typography
                            fontSize={16}
                            color={'header.main'}
                            typography="h4"
                            sx={(theme) => ({
                                mb: 1.45,
                                fontFamily: theme.typography.fontFamily,
                            })}
                        >
                            {titleMessages?.work?.greeting[defaultLang]}
                        </Typography>
                        <Box
                            sx={(theme) => ({
                                color: 'header.main',
                                width: '100%',
                                p: {
                                    fontFamily: theme.typography.fontFamily,
                                }
                            })}
                        >
                            {titleMessages?.work?.result[defaultLang] ? parse(titleMessages.work.result[defaultLang]) : ''}
                        </Box>
                        </>}
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '10px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                            }}
                        >
                            {visitorMessages.map(({active, button, message}, index) => {
                                if (!active || !message?.[defaultLang] || !button?.[defaultLang]) return null;

                                const messageByLang = message[defaultLang];
                                const buttonTextByLang = button[defaultLang];
                                return (
                                    <Chip
                                        key={`${buttonTextByLang}-${index}`}
                                        label={buttonTextByLang}
                                        variant="outlined"
                                        text={messageByLang}
                                        onClick={() => {
                                            if (isMessageSended) return;
                                            onSendStartMessage(messageByLang);
                                            setMessageSended(true);
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </>
                )}
            </Box>
            {/* <Details title={'Напишите нам в мессенджер'}>
          <Messengers />
        </Details> */}
        </>
    );
};

export const Typing = memo(
    ({defaultLang, text = translates[defaultLang].writing}) => (
        <Typography
            sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 'auto',
                mb: 1.4,
                columnGap: '8px',
                color: 'header.main',
                fontWeight: 'bold',
            }}
        >
            {text}{' '}
            <Box
                component={AnimatedDotsIcon}
                sx={{
                    height: '8px',
                }}
            />
        </Typography>
    )
);

const ArntFreeOperanors = ({defaultLang}) => (
    <Box
        sx={(theme) => ({
            padding: theme.spacing(1.45),
            margin: '10px auto',
            borderRadius: '5px',
            border: `1px solid ${theme.palette.error.alternate}`,
        })}
    >
        {translates[defaultLang].no_free_operators}
    </Box>
);

const SYSTEM_COLORS = {
    grey: {
        text: 'rgba(145, 151, 165, 1)',
        line: 'rgba(145, 151, 165, 0.35)',
    },
    red: {
        text: 'rgba(247, 142, 152, 1)',
        line: 'rgba(247, 142, 152, 0.35)',
    },
};

const SystemMessage = ({text, time, color}) => {
    const palette = SYSTEM_COLORS[color] || SYSTEM_COLORS.grey;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                width: '100%',
                padding: '4px 0 8px',
                margin: '10px 0',
                color: palette.text,
            }}
        >
            <Box sx={{flex: 1, height: '1px', backgroundColor: palette.line}}/>
            <Typography
                sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '12px',
                    lineHeight: '18px',
                    fontWeight: 400,
                    whiteSpace: 'pre-line',
                    color: palette.text,
                    textAlign: 'center',
                    verticalAlign: 'middle',
                }}
            >
                {text}
                {time ? ` ${time}` : ''}
            </Typography>
            <Box sx={{flex: 1, height: '1px', backgroundColor: palette.line}}/>
        </Box>
    );
};


const ActionBlock = ({onClick, defaultLang}) => (
    <Box
        sx={(theme) => ({
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: theme.palette.primary.text,
            borderRadius: '5px',
            margin: 'auto auto 10px',
            padding: theme.spacing(1.45),
            border: `1px solid ${theme.palette.primary.main}`,
            typography: 'h5',
        })}
    >
        <Typography sx={(theme) => ({
            fontFamily: theme.typography.fontFamily,
        })}>
            {translates[defaultLang].give_us_your_data}
        </Typography>
        <Button
            onClick={onClick}
            variant="contained"
            sx={(theme) => ({
                typography: 'subtitle1',
                fontFamily: theme.typography.fontFamily,
            })}
        >
            {translates[defaultLang].fill}
        </Button>
    </Box>
);
